import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Sparkles, Shield, Eye, EyeOff, RefreshCw, Bot } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  
  // CAPTCHA state
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState(false);

  // Load remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('socyy_remembered_email');
    const wasRemembered = localStorage.getItem('socyy_remember_me') === 'true';
    if (rememberedEmail && wasRemembered) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
    // Load initial CAPTCHA
    loadCaptcha();
  }, []);

  const loadCaptcha = async () => {
    setCaptchaError(false);
    try {
      const res = await axios.get(`${API_URL}/spam/captcha/generate`);
      setCaptcha(res.data);
      setCaptchaAnswer('');
      setCaptchaVerified(false);
    } catch (error) {
      console.error('Failed to load CAPTCHA');
      setCaptchaError(true);
    }
  };

  const verifyCaptcha = async () => {
    if (!captchaAnswer.trim()) {
      toast.error('Please enter the answer');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/spam/captcha/verify`, {
        captcha_token: captcha.token,
        captcha_answer: captchaAnswer
      });
      if (res.data.verified) {
        setCaptchaVerified(true);
        setCaptchaToken(res.data.verification_token);
        toast.success('Verified! You can now log in.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect answer');
      loadCaptcha(); // Load new CAPTCHA
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Check CAPTCHA first
    if (!captchaVerified) {
      toast.error('Please complete the CAPTCHA verification first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        ...formData,
        remember_me: rememberMe,
        captcha_token: captchaToken
      }, {
        withCredentials: true,
      });
      
      // Handle Remember Me
      if (rememberMe) {
        localStorage.setItem('socyy_remembered_email', formData.email);
        localStorage.setItem('socyy_remember_me', 'true');
      } else {
        localStorage.removeItem('socyy_remembered_email');
        localStorage.removeItem('socyy_remember_me');
      }
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        toast.info('Please enter your 2FA code');
      } else if (response.data.user?.account_status === 'pending_liveness' || 
                 (response.data.user?.liveness_required && !response.data.user?.liveness_verified)) {
        // Redirect to liveness verification for new accounts
        toast.info('Please complete identity verification to activate your account');
        navigate('/verify/liveness', { state: { user: response.data.user, fromRegistration: true } });
      } else {
        // Store session token for authenticated pages
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
          localStorage.setItem('token', response.data.session_token);
          localStorage.setItem('socyy_token', response.data.session_token);
        }
        toast.success('Welcome back!');
        navigate('/dashboard', { state: { user: response.data.user } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FALogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/security/2fa/login`, {
        email: formData.email,
        password: formData.password,
        code: twoFACode
      }, {
        withCredentials: true,
      });
      // Store session token for authenticated pages
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
        localStorage.setItem('token', response.data.session_token);
        localStorage.setItem('socyy_token', response.data.session_token);
      }
      toast.success('Welcome back!');
      navigate('/dashboard', { state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || '2FA verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-white/70 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/20">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-heading font-black text-gray-900">Socyy</h1>
          </div>

          <h2 className="text-2xl font-heading font-bold text-center mb-6 text-gray-900" data-testid="login-title">
            {requires2FA ? 'Two-Factor Authentication' : 'Welcome Back!'}
          </h2>

          {requires2FA ? (
            <form onSubmit={handle2FALogin} className="space-y-4 mb-6" data-testid="2fa-form">
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-800">2FA Required</span>
                </div>
                <p className="text-sm text-blue-700">
                  Enter the 6-digit code from your authenticator app or a backup code
                </p>
              </div>
              <div>
                <Label htmlFor="2fa-code" className="font-semibold text-gray-700">Authentication Code</Label>
                <Input
                  id="2fa-code"
                  type="text"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  className="rounded-xl border-2 h-12 mt-1 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={8}
                  required
                  data-testid="2fa-code-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-primary hover:bg-primary-700 text-white font-bold h-12 shadow-lg hover:scale-105 transition-transform"
                data-testid="2fa-submit-btn"
              >
                {loading ? 'Verifying...' : 'Verify & Log In'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setRequires2FA(false);
                  setTwoFACode('');
                }}
                className="w-full"
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Button>
            </form>
          ) : (
            <>
              <form onSubmit={handleLogin} className="space-y-4 mb-6" data-testid="login-form">
                <div>
                  <Label htmlFor="email" className="font-semibold text-gray-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="rounded-xl border-2 h-12 mt-1"
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="font-semibold text-gray-700">Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="rounded-xl border-2 h-12 pr-12"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember-me"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked)}
                      data-testid="remember-me-checkbox"
                    />
                    <label
                      htmlFor="remember-me"
                      className="text-sm font-medium text-gray-700 cursor-pointer select-none"
                    >
                      Remember me
                    </label>
                  </div>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-primary hover:text-primary-700 font-semibold"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                
                {/* CAPTCHA Section */}
                <div className="bg-gray-50 p-4 rounded-xl border-2 border-gray-200" data-testid="captcha-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-5 h-5 text-gray-600" />
                    <span className="font-semibold text-gray-700">Verify you're human</span>
                    {captchaVerified && (
                      <span className="ml-auto text-green-600 text-sm font-medium flex items-center gap-1">
                        ✓ Verified
                      </span>
                    )}
                  </div>
                  
                  {!captchaVerified ? (
                    <>
                      {captchaError ? (
                        <div className="text-center py-3 space-y-2">
                          <p className="text-sm text-red-500">Failed to load verification challenge</p>
                          <Button type="button" onClick={loadCaptcha} size="sm" variant="outline" data-testid="captcha-retry-btn">
                            Try Again
                          </Button>
                        </div>
                      ) : captcha ? (
                        <div className="space-y-3">
                          {/* Challenge display */}
                          <div className="bg-white p-4 rounded-lg border">
                            <p className="text-sm text-gray-500 mb-1 uppercase tracking-wide">
                              {captcha.type === 'word_puzzle' ? 'Word Puzzle' :
                               captcha.type === 'pattern' ? 'Number Pattern' :
                               captcha.type === 'sequence' ? 'Letter Sequence' :
                               captcha.type === 'category' ? 'Odd One Out' :
                               captcha.type === 'logic' ? 'Logic Question' :
                               'Math Problem'}
                            </p>
                            <p className="text-base font-medium text-gray-800">
                              {captcha.challenge}
                            </p>
                            {captcha.hint && (
                              <p className="text-xs text-gray-400 mt-2 italic">
                                💡 {captcha.hint}
                              </p>
                            )}
                          </div>
                          
                          {/* Answer input */}
                          <div className="flex items-center gap-3">
                            <Input
                              type={captcha.type === 'math' || captcha.type === 'pattern' ? 'number' : 'text'}
                              value={captchaAnswer}
                              onChange={(e) => setCaptchaAnswer(e.target.value)}
                              className="flex-1 text-center font-bold"
                              placeholder="Your answer"
                              data-testid="captcha-answer-input"
                              autoComplete="off"
                            />
                            <Button
                              type="button"
                              onClick={verifyCaptcha}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              data-testid="verify-captcha-btn"
                            >
                              Verify
                            </Button>
                            <Button
                              type="button"
                              onClick={loadCaptcha}
                              variant="ghost"
                              size="sm"
                              title="Get new challenge"
                              data-testid="refresh-captcha-btn"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-3">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                          <p className="text-xs text-gray-400 mt-2">Loading challenge...</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-green-600 py-2">
                      <Shield className="w-6 h-6 mx-auto mb-1" />
                      <span className="text-sm">Human verified</span>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading || !captchaVerified}
                  className="w-full rounded-full bg-primary hover:bg-primary-700 text-white font-bold h-12 shadow-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Logging in...' : 'Log In'}
                </Button>
              </form>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full rounded-full border-2 h-12 font-bold hover:bg-gray-50 transition-all"
                data-testid="login-google-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </>
          )}

          <p className="text-center text-sm text-gray-600 mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="font-bold text-primary hover:text-primary-700" data-testid="login-register-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="absolute bottom-0 w-full py-4 text-center text-xs text-gray-500">
        <p>© {new Date().getFullYear()} Socyy. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Login;
